import { useState, useEffect } from "react";
import { useLocaleNavigate } from "@/hooks/useLocaleNavigate";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import Header from "@/components/layout/Header";
import { useAuth } from "@/hooks/useAuth";
import { useProvider } from "@/hooks/useProvider";
import { useStackTags } from "@/hooks/useStackTags";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Plus, Trash2, Star } from "lucide-react";

const inputClass =
  "w-full h-11 px-4 border border-border bg-background text-body focus:outline-none focus:border-primary transition-colors";

const CATEGORY_OPTIONS = [
  { key: "managed_service", label: "Wartung & Pflege" },
  { key: "project", label: "Projekt" },
  { key: "security", label: "Sicherheit" },
  { key: "cloud", label: "Neubau & Installation" },
  { key: "support", label: "Reparatur & Notdienst" },
];

const COMPANY_TYPE_OPTIONS = [
  "GmbH", "UG", "AG", "GmbH & Co. KG", "KG", "OHG",
  "Einzelunternehmen", "Freiberufler", "GbR", "eG", "Sonstige",
];

interface Contact {
  id: string;
  name: string;
  role: string;
  email: string;
  phone: string;
  is_primary: boolean;
}

const ProviderProfileEdit = () => {
  const { t } = useTranslation();
  const navigate = useLocaleNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const { profile, isLoadingProfile } = useProvider();
  const { groupedByCategory, isLoading: tagsLoading } = useStackTags();

  const [form, setForm] = useState({
    company_name: "",
    company_type: "",
    legal_name: "",
    vat_id: "",
    website: "",
    contact_name: "",
    contact_role: "",
    contact_email: "",
    phone: "",
    address_line1: "",
    postal_code: "",
    city: "",
    country: "DE",
    monthly_opportunity_limit: 20,
  });
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedStacks, setSelectedStacks] = useState<string[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!authLoading && !user) navigate("/auth");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (profile && !loaded) {
      setForm({
        company_name: profile.company_name || "",
        company_type: (profile as any).company_type || "",
        legal_name: profile.legal_name || "",
        vat_id: profile.vat_id || "",
        website: profile.website || "",
        contact_name: profile.contact_name || "",
        contact_role: profile.contact_role || "",
        contact_email: profile.contact_email || "",
        phone: profile.phone || "",
        address_line1: profile.address_line1 || "",
        postal_code: profile.postal_code || "",
        city: profile.city || "",
        country: profile.country || "DE",
        monthly_opportunity_limit: profile.monthly_opportunity_limit || 20,
      });
      const profileContacts = (profile as any).contacts;
      if (Array.isArray(profileContacts)) setContacts(profileContacts);
      loadProviderData();
      setLoaded(true);
    }
  }, [profile, loaded]);

  async function loadProviderData() {
    if (!profile) return;
    const [catsRes, stacksRes] = await Promise.all([
      supabase.from("provider_categories").select("category").eq("provider_id", profile.id),
      supabase.from("provider_stacks").select("stack_tag_id").eq("provider_id", profile.id),
    ]);
    setSelectedCategories((catsRes.data || []).map((c: any) => c.category));
    setSelectedStacks((stacksRes.data || []).map((s: any) => s.stack_tag_id));
  }

  const update = (field: string, value: string | number) =>
    setForm((f) => ({ ...f, [field]: value }));

  const toggleCategory = (key: string) =>
    setSelectedCategories((prev) => prev.includes(key) ? prev.filter((c) => c !== key) : [...prev, key]);

  const toggleStack = (id: string) =>
    setSelectedStacks((prev) => prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]);

  const addContact = () =>
    setContacts((prev) => [...prev, { id: crypto.randomUUID(), name: "", role: "", email: "", phone: "", is_primary: prev.length === 0 }]);

  const removeContact = (id: string) =>
    setContacts((prev) => {
      const remaining = prev.filter((c) => c.id !== id);
      if (remaining.length > 0 && !remaining.some((c) => c.is_primary)) remaining[0].is_primary = true;
      return remaining;
    });

  const updateContact = (id: string, field: keyof Contact, value: any) =>
    setContacts((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)));

  const setPrimary = (id: string) =>
    setContacts((prev) => prev.map((c) => ({ ...c, is_primary: c.id === id })));

  const handleSave = async () => {
    if (!profile) return;
    setIsSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error(t("profileEdit.notLoggedIn"));

      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/provider-api/me`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({
            company_name: form.company_name.trim(),
            company_type: form.company_type || null,
            contact_name: form.contact_name.trim(),
            contact_role: form.contact_role.trim() || null,
            contact_email: form.contact_email.trim() || null,
            phone: form.phone.trim() || null,
            legal_name: form.legal_name.trim() || null,
            vat_id: form.vat_id.trim() || null,
            website: form.website.trim() || null,
            address_line1: form.address_line1.trim() || null,
            postal_code: form.postal_code.trim() || null,
            city: form.city.trim() || null,
            country: form.country.trim() || "DE",
            monthly_opportunity_limit: form.monthly_opportunity_limit,
            contacts: contacts,
            categories: selectedCategories,
            stack_tags: selectedStacks,
          }),
        }
      );

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: res.statusText }));
        throw new Error(err.error || res.statusText);
      }

      toast.success(t("profileEdit.saved"));
      navigate("/dashboard");
    } catch (error: any) {
      console.error("Profile save error:", error);
      toast.error(error.message || t("profileEdit.saveError"));
    } finally {
      setIsSaving(false);
    }
  };

  if (authLoading || isLoadingProfile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-caption">{t("common.loading")}</p>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <p className="text-caption">{t("profileEdit.noProfile")}</p>
          <Button variant="commit" onClick={() => navigate("/onboarding")}>{t("profileEdit.createProfile")}</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-24 pb-12 section-padding">
        <div className="max-w-2xl mx-auto space-y-10">
          <div>
            <h1 className="text-headline">{t("profileEdit.title")}</h1>
            <p className="text-caption mt-1">{t("profileEdit.subtitle")}</p>
          </div>

          {/* Stammdaten */}
          <section className="p-6 border border-border bg-card space-y-5">
            <h2 className="text-subheadline">{t("profileEdit.companySection")}</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-micro block">{t("profileEdit.companyName")}</label>
                  <input type="text" value={form.company_name} onChange={(e) => update("company_name", e.target.value)} className={inputClass} />
                </div>
                <div className="space-y-2">
                  <label className="text-micro block">{t("profileEdit.companyType")}</label>
                  <select value={form.company_type} onChange={(e) => update("company_type", e.target.value)} className={inputClass}>
                    <option value="">{t("profileEdit.companyTypePlaceholder")}</option>
                    {COMPANY_TYPE_OPTIONS.map((ct) => <option key={ct} value={ct}>{ct}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-micro block">{t("profileEdit.legalName")}</label>
                  <input type="text" value={form.legal_name} onChange={(e) => update("legal_name", e.target.value)} className={inputClass} placeholder={t("profileEdit.legalNamePlaceholder")} />
                </div>
                <div className="space-y-2">
                  <label className="text-micro block">{t("profileEdit.vatId")}</label>
                  <input type="text" value={form.vat_id} onChange={(e) => update("vat_id", e.target.value)} className={inputClass} placeholder={t("profileEdit.vatIdPlaceholder")} />
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-micro block">{t("profileEdit.website")}</label>
                <input type="text" value={form.website} onChange={(e) => update("website", e.target.value)} className={inputClass} placeholder="https://..." />
              </div>
            </div>
          </section>

          {/* Adresse */}
          <section className="p-6 border border-border bg-card space-y-5">
            <h2 className="text-subheadline">{t("profileEdit.addressSection")}</h2>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-micro block">{t("profileEdit.street")}</label>
                <input type="text" value={form.address_line1} onChange={(e) => update("address_line1", e.target.value)} className={inputClass} />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <label className="text-micro block">{t("profileEdit.postalCode")}</label>
                  <input type="text" value={form.postal_code} onChange={(e) => update("postal_code", e.target.value)} className={inputClass} />
                </div>
                <div className="space-y-2">
                  <label className="text-micro block">{t("profileEdit.city")}</label>
                  <input type="text" value={form.city} onChange={(e) => update("city", e.target.value)} className={inputClass} />
                </div>
                <div className="space-y-2">
                  <label className="text-micro block">{t("profileEdit.country")}</label>
                  <input type="text" value={form.country} onChange={(e) => update("country", e.target.value)} className={inputClass} />
                </div>
              </div>
            </div>
          </section>

          {/* Hauptkontakt */}
          <section className="p-6 border border-border bg-card space-y-5">
            <h2 className="text-subheadline">{t("profileEdit.contactSection")}</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-micro block">{t("profileEdit.contactName")}</label>
                  <input type="text" value={form.contact_name} onChange={(e) => update("contact_name", e.target.value)} className={inputClass} />
                </div>
                <div className="space-y-2">
                  <label className="text-micro block">{t("profileEdit.contactRole")}</label>
                  <input type="text" value={form.contact_role} onChange={(e) => update("contact_role", e.target.value)} className={inputClass} placeholder={t("profileEdit.contactRolePlaceholder")} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <label className="text-micro block">{t("profileEdit.contactEmail")}</label>
                  <input type="email" value={form.contact_email} onChange={(e) => update("contact_email", e.target.value)} className={inputClass} />
                </div>
                <div className="space-y-2">
                  <label className="text-micro block">{t("profileEdit.contactPhone")}</label>
                  <input type="tel" value={form.phone} onChange={(e) => update("phone", e.target.value)} className={inputClass} placeholder={t("profileEdit.contactPhonePlaceholder")} />
                </div>
              </div>
            </div>
          </section>

          {/* Weitere Kontakte */}
          <section className="p-6 border border-border bg-card space-y-5">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-subheadline">{t("profileEdit.additionalContacts")}</h2>
                <p className="text-caption mt-1">{t("profileEdit.additionalContactsDesc")}</p>
              </div>
              <Button type="button" variant="quiet" size="sm" onClick={addContact}>
                <Plus className="w-4 h-4 mr-1" /> {t("profileEdit.addContact")}
              </Button>
            </div>
            {contacts.length === 0 && (
              <div className="p-8 border border-dashed border-border text-center">
                <p className="text-caption mb-4">{t("profileEdit.noContacts")}</p>
                <Button type="button" variant="quiet" size="sm" onClick={addContact}>
                  <Plus className="w-4 h-4 mr-1" /> {t("profileEdit.createContact")}
                </Button>
              </div>
            )}
            {contacts.map((contact, idx) => (
              <div key={contact.id} className="border border-border p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-micro">{t("profileEdit.contactLabel", { n: idx + 1 })}</span>
                    {contact.is_primary && (
                      <span className="text-xs px-2 py-0.5 bg-accent/10 text-accent border border-accent/20">{t("profileEdit.primaryContact")}</span>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {!contact.is_primary && (
                      <button type="button" onClick={() => setPrimary(contact.id)} title={t("profileEdit.primaryContact")} className="p-1.5 text-muted-foreground hover:text-accent transition-colors">
                        <Star className="w-4 h-4" />
                      </button>
                    )}
                    <button type="button" onClick={() => removeContact(contact.id)} className="p-1.5 text-muted-foreground hover:text-destructive transition-colors">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-micro block">{t("profileEdit.nameRequired")}</label>
                    <input value={contact.name} onChange={(e) => updateContact(contact.id, "name", e.target.value)} className={inputClass} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-micro block">{t("profileEdit.contactRole")}</label>
                    <input value={contact.role} onChange={(e) => updateContact(contact.id, "role", e.target.value)} placeholder="z.B. Vertrieb" className={inputClass} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-micro block">{t("profileEdit.emailRequired")}</label>
                    <input type="email" value={contact.email} onChange={(e) => updateContact(contact.id, "email", e.target.value)} className={inputClass} />
                  </div>
                  <div className="space-y-2">
                    <label className="text-micro block">{t("profileEdit.contactPhone")}</label>
                    <input value={contact.phone} onChange={(e) => updateContact(contact.id, "phone", e.target.value)} placeholder={t("profileEdit.contactPhonePlaceholder")} className={inputClass} />
                  </div>
                </div>
              </div>
            ))}
          </section>

          {/* Dienstleistungen */}
          <section className="p-6 border border-border bg-card space-y-5">
            <h2 className="text-subheadline">{t("profileEdit.tradesSection")}</h2>
            <div className="space-y-2">
              <label className="text-micro block">{t("profileEdit.tradesLabel")}</label>
              <div className="flex flex-wrap gap-2">
                {CATEGORY_OPTIONS.map((cat) => (
                  <button
                    key={cat.key}
                    type="button"
                    onClick={() => toggleCategory(cat.key)}
                    className={cn(
                      "px-3 py-1.5 text-sm border transition-colors",
                      selectedCategories.includes(cat.key)
                        ? "bg-primary text-primary-foreground border-primary"
                        : "bg-background border-border text-muted-foreground hover:border-primary/50"
                    )}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-micro block">{t("profileEdit.stackLabel")}</label>
              {tagsLoading ? (
                <p className="text-caption">{t("profileEdit.stackLoading")}</p>
              ) : (
                <div className="space-y-4">
                  {Object.entries(groupedByCategory).map(([category, tags]) => (
                    <div key={category}>
                      <p className="text-xs font-medium text-muted-foreground mb-2">{category}</p>
                      <div className="flex flex-wrap gap-1.5">
                        {tags.map((tag) => (
                          <button
                            key={tag.id}
                            type="button"
                            onClick={() => toggleStack(tag.id)}
                            className={cn(
                              "px-2.5 py-1 text-xs border transition-colors",
                              selectedStacks.includes(tag.id)
                                ? "bg-primary text-primary-foreground border-primary"
                                : "bg-background border-border text-muted-foreground hover:border-primary/50"
                            )}
                          >
                            {tag.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* Limits */}
          <section className="p-6 border border-border bg-card space-y-5">
            <h2 className="text-subheadline">{t("profileEdit.limitsSection")}</h2>
            <p className="text-caption">{t("profileEdit.monthlyLimitDesc")}</p>
            <div className="flex items-center gap-4">
              <input
                type="number"
                min={1}
                value={form.monthly_opportunity_limit}
                onChange={(e) => update("monthly_opportunity_limit", Math.max(1, parseInt(e.target.value) || 1))}
                className={cn(inputClass, "w-24 text-center")}
              />
              <span className="text-caption">{t("profileEdit.monthlyLimitUnit")}</span>
            </div>
          </section>

          {/* Actions */}
          <div className="flex gap-4">
            <Button
              variant="commit"
              size="lg"
              className="flex-1"
              onClick={handleSave}
              disabled={isSaving || !form.company_name.trim() || !form.contact_name.trim()}
            >
              {isSaving ? t("profileEdit.saving") : t("profileEdit.saveBtn")}
            </Button>
            <Button variant="quiet" size="lg" onClick={() => navigate("/dashboard")}>
              {t("common.cancel")}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProviderProfileEdit;